import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus, Trash2, Copy, Shield, Users, Globe, Zap } from 'lucide-react';
import { partnersApi, type CreatePartnerData, type PartnerCredentials } from '../../api/endpoints/partners';
import { queryClient } from '../../providers/QueryProvider';
import { StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';

const API_NAMES = ['me', 'tunnels', 'tunnels_count', 'proxy', 'broadcast', 'call', 'call_wildcard'] as const;

export function PartnersPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [credentials, setCredentials] = useState<PartnerCredentials | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formContactName, setFormContactName] = useState('');
  const [formContactEmail, setFormContactEmail] = useState('');
  const [formIps, setFormIps] = useState('');
  const [formWebhookUrl, setFormWebhookUrl] = useState('');
  const [formRequireIp, setFormRequireIp] = useState(true);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['partners'],
    queryFn: () => partnersApi.list(),
  });

  const { data: statsData } = useQuery({
    queryKey: ['partners-stats'],
    queryFn: () => partnersApi.stats(),
  });

  const createMutation = useMutation({
    mutationFn: (formData: CreatePartnerData) => partnersApi.create(formData),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['partners-stats'] });
      setShowCreate(false);
      setCredentials(result.credentials);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => partnersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['partners-stats'] });
    },
  });

  function resetForm() {
    setFormName('');
    setFormDescription('');
    setFormContactName('');
    setFormContactEmail('');
    setFormIps('');
    setFormWebhookUrl('');
    setFormRequireIp(true);
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const ips = formIps.split('\n').map(ip => ip.trim()).filter(Boolean);
    createMutation.mutate({
      name: formName,
      description: formDescription || undefined,
      contact: {
        name: formContactName || undefined,
        email: formContactEmail || undefined,
      },
      whitelisted_ips: ips.length > 0 ? ips : undefined,
      default_webhook_url: formWebhookUrl || undefined,
      settings: { require_ip_whitelist: formRequireIp },
    });
  }

  function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    if (confirm('Delete this partner? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  }

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  const partners = data?.partners || [];
  const stats = statsData?.stats;
  const totalIps = partners.reduce((sum, p) => sum + (p.whitelisted_ips?.length || 0), 0);
  const totalTunnels = partners.reduce((sum, p) => sum + (p.authorized_tunnels?.length || 0), 0);

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Partner Integrations</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Manage partner access to tunnels with IP whitelisting</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
            <RefreshCw size={14} />
            Refresh
          </button>
          <button onClick={() => setShowCreate(true)} className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs">
            <Plus size={14} />
            Add Partner
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Partners" value={stats?.total ?? partners.length} icon={<Users size={18} />} color="purple" />
        <StatCard label="Active" value={stats?.active ?? partners.filter(p => p.status === 'active').length} icon={<Shield size={18} />} color="green" />
        <StatCard label="Whitelisted IPs" value={totalIps} icon={<Globe size={18} />} color="blue" />
        <StatCard label="Authorized Tunnels" value={totalTunnels} icon={<Zap size={18} />} color="yellow" />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        <Table>
          <Thead>
            <tr>
              <Th>Partner</Th>
              <Th>Partner ID</Th>
              <Th>Status</Th>
              <Th>Whitelisted IPs</Th>
              <Th>Tunnels</Th>
              <Th>APIs Enabled</Th>
              <Th>Requests</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {partners.map(partner => {
              const enabledCount = partner.settings?.enabled_apis
                ? API_NAMES.filter(k => partner.settings.enabled_apis[k] !== false).length
                : 7;
              return (
                <Tr key={partner._id} onClick={() => navigate(`/partners/${partner._id}`)}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 dark:text-purple-400 font-bold text-xs">{partner.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="font-medium text-purple-600 dark:text-purple-400">{partner.name}</span>
                        <p className="text-[10px] text-gray-400">{partner.contact?.email || 'No email'}</p>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">{partner.partner_id}</code>
                  </Td>
                  <Td>
                    <Badge
                      variant={partner.status === 'active' ? 'green' : partner.status === 'suspended' ? 'red' : 'yellow'}
                      dot
                    >
                      {partner.status}
                    </Badge>
                  </Td>
                  <Td>
                    <span className="font-medium">{partner.whitelisted_ips?.length || 0}</span>
                    <span className="text-gray-400 ml-1">IPs</span>
                  </Td>
                  <Td>
                    <span className="font-medium">{partner.authorized_tunnels?.length || 0}</span>
                    <span className="text-gray-400 ml-1">clients</span>
                  </Td>
                  <Td>
                    <Badge variant={enabledCount === 7 ? 'green' : enabledCount === 0 ? 'red' : 'yellow'}>
                      {enabledCount}/7
                    </Badge>
                  </Td>
                  <Td>
                    <span className="text-gray-500 dark:text-gray-400">{partner.usage?.total_requests || 0}</span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      <button onClick={() => navigate(`/partners/${partner._id}`)} className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium">
                        Manage
                      </button>
                      <button onClick={(e) => handleDelete(e, partner._id)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </Td>
                </Tr>
              );
            })}
            {partners.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No partners yet. Add one to get started.</td></tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Create Partner Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add Partner" wide>
        <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Partner Name *</label>
            <input
              type="text"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              required
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              placeholder="Partner company name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              value={formDescription}
              onChange={e => setFormDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
              <input
                type="text"
                value={formContactName}
                onChange={e => setFormContactName(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Email</label>
              <input
                type="email"
                value={formContactEmail}
                onChange={e => setFormContactEmail(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Whitelisted IPs (one per line)</label>
            <textarea
              value={formIps}
              onChange={e => setFormIps(e.target.value)}
              rows={3}
              placeholder={"192.168.1.1\n10.0.0.0/8"}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs font-mono dark:bg-gray-700 dark:text-white"
            />
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Only requests from these IPs will be allowed</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Default Webhook URL</label>
            <input
              type="url"
              value={formWebhookUrl}
              onChange={e => setFormWebhookUrl(e.target.value)}
              placeholder="https://partner.com/webhook"
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
            />
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formRequireIp}
              onChange={e => setFormRequireIp(e.target.checked)}
              className="rounded text-purple-600 dark:bg-gray-700"
            />
            <span className="text-xs text-gray-700 dark:text-gray-300">Require IP whitelist (recommended)</span>
          </label>
          {createMutation.error && (
            <p className="text-xs text-red-600 dark:text-red-400">{(createMutation.error as Error).message}</p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isPending} className="px-4 py-1.5 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700 disabled:opacity-50">
              {createMutation.isPending ? 'Creating...' : 'Create Partner'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Credentials Modal */}
      <Modal open={!!credentials} onClose={() => setCredentials(null)} title="Partner Created!">
        <div className="space-y-3">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
            <p className="text-yellow-800 dark:text-yellow-300 text-xs font-medium">Save these credentials now! They will not be shown again.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Partner ID</label>
            <div className="flex">
              <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs text-gray-800 dark:text-gray-200">{credentials?.partner_id}</code>
              <button onClick={() => copyText(credentials?.partner_id || '')} className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 text-xs">
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">API Key</label>
            <div className="flex">
              <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs overflow-x-auto text-gray-800 dark:text-gray-200">{credentials?.api_key}</code>
              <button onClick={() => copyText(credentials?.api_key || '')} className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 text-xs">
                <Copy size={14} />
              </button>
            </div>
          </div>
          {credentials?.webhook_secret && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Webhook Secret</label>
              <div className="flex">
                <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs overflow-x-auto text-gray-800 dark:text-gray-200">{credentials.webhook_secret}</code>
                <button onClick={() => copyText(credentials.webhook_secret!)} className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 text-xs">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}
          <button onClick={() => setCredentials(null)} className="w-full px-4 py-2 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700 mt-2">
            Done
          </button>
        </div>
      </Modal>
    </div>
  );
}
